import type { WhyLine } from '../sim/playEngine';
import { el, onTap } from './dom';

/**
 * Result banner and "why" card (GDD 6.9, reference 07): a slanted brush banner with the headline,
 * the why rows below and NEXT PLAY. NEXT PLAY or a quick tap anywhere on it moves on; a finger
 * held on it keeps it up to read. It auto-advances otherwise.
 */
export class Banner {
  readonly root = el('div', 'banner');
  private readonly head = el('div', 'banner-head');
  private readonly why = el('div', 'why');
  private readonly rows = el('div', 'why-rows');
  private readonly next = el('button', 'btn btn-primary btn-next', 'NEXT PLAY');
  private pressId = -1;
  private pressAt = 0;

  /** holdSeconds: a press shorter than this is a tap. */
  constructor(onNext: () => void, holdSeconds: number) {
    const tag = el('div', 'why-tag', 'WHY');
    this.why.append(tag, this.rows);
    this.root.append(this.head, this.why, this.next);
    this.root.dataset.testid = 'result';
    this.next.dataset.testid = 'next-play';
    this.root.hidden = true;
    onTap(this.next, () => onNext());
    this.root.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || (e.target as Element).closest('.btn-next')) return;
      this.pressId = e.pointerId;
      this.pressAt = performance.now();
      // A mouse released off the banner still ends the hold.
      this.root.setPointerCapture?.(e.pointerId);
    });
    const up = (e: PointerEvent, tap: boolean) => {
      if (e.pointerId !== this.pressId) return;
      this.pressId = -1;
      if (tap && (performance.now() - this.pressAt) / 1000 < holdSeconds) onNext();
    };
    this.root.addEventListener('pointerup', (e) => up(e, true));
    this.root.addEventListener('pointercancel', (e) => up(e, false));
    this.root.addEventListener('lostpointercapture', (e) => up(e, false));
  }

  show(headline: string, why: WhyLine[], button: string, tone: 'good' | 'bad'): void {
    this.head.replaceChildren(...headline.split(', ').map((line) => el('div', '', line)));
    this.head.dataset.tone = tone;
    this.rows.replaceChildren(
      ...why.map((w) => {
        const row = el('div', 'why-row');
        row.append(el('span', 'why-label', w.label), el('span', `why-value tone-${w.tone}`, w.value));
        return row;
      }),
    );
    this.why.hidden = why.length === 0;
    this.next.textContent = button;
    this.pressId = -1;
    this.root.hidden = false;
    this.root.classList.remove('in');
    void this.root.offsetWidth;
    this.root.classList.add('in');
  }

  hide(): void {
    this.root.hidden = true;
  }

  /** A finger is on the banner. */
  get isHeld(): boolean {
    return this.pressId >= 0;
  }
}

/** Title screen: logo, team names, PLAY. The first tap also unlocks audio. */
export interface TitleContent {
  /** The brand's logo above the title (its own animation runs), or null for the brand name as text. */
  logo: SVGSVGElement | null;
  brandName: string;
  /** The big lines of type; accent lines take the brand accent. */
  lines: { text: string; accent?: boolean }[];
  /** Under the title: the mode and the matchup. */
  sub: string;
  /** The hero picture behind it all; without one the stadium shows through. */
  hero: HTMLImageElement | null;
}

/** How to play, in the order the game teaches it. */
const HOW_TO_PLAY = [
  'Pick a play, then tap SNAP.',
  'Tap a receiver to throw. Green is open; hold for a longer throw.',
  'Tap as the ring closes to make the catch.',
  'After the catch, drag to steer, swipe to juke, truck or spin, and hold SPRINT.',
];

export class TitleScreen {
  readonly root = el('div', 'title');

  constructor(c: TitleContent, onPlay: () => void) {
    if (c.hero) {
      c.hero.className = 'title-hero';
      c.hero.alt = '';
      this.root.append(c.hero, el('div', 'title-shade'));
      this.root.classList.add('has-hero');
    }
    const box = el('div', 'title-box');
    let brandLine: Element;
    if (c.logo) {
      c.logo.classList.add('title-logo');
      c.logo.setAttribute('role', 'img');
      c.logo.setAttribute('aria-label', c.brandName);
      brandLine = c.logo;
    } else brandLine = el('div', 'title-kicker', c.brandName.toUpperCase());
    const word = el('div', 'title-word');
    for (const line of c.lines) word.append(el('div', line.accent ? 'title-word-accent' : '', line.text.toUpperCase()));
    const play = el('button', 'btn btn-primary btn-start', 'START');
    play.dataset.testid = 'play';
    onTap(play, () => onPlay());
    const how = el('button', 'title-how', 'HOW TO PLAY');
    how.dataset.testid = 'how-to-play';
    how.setAttribute('aria-expanded', 'false');
    const steps = el('ol', 'title-steps');
    for (const line of HOW_TO_PLAY) steps.append(el('li', '', line));
    steps.hidden = true;
    onTap(how, () => {
      steps.hidden = !steps.hidden;
      how.setAttribute('aria-expanded', String(!steps.hidden));
    });
    box.append(
      brandLine,
      word,
      el('div', 'title-sub', c.sub.toUpperCase()),
      play,
      el('div', 'title-help', '4 quarters, 1 minute each. You have the ball.'),
      how,
      steps,
    );
    this.root.append(box);
  }

  hide(): void {
    this.root.hidden = true;
  }
}

/** Ignores taps for a moment after a card appears, so a tap meant for the screen before cannot pick a button. */
function guarded(guardSeconds: number, shownAt: () => number, fn: () => void): () => void {
  return () => {
    if ((performance.now() - shownAt()) / 1000 >= guardSeconds) fn();
  };
}

/** 4th down: three big buttons (GDD section 4). */
export class FourthDown {
  readonly root = el('div', 'fourth');
  private readonly fg = el('button', 'btn btn-ghost btn-big');
  private readonly sub = el('div', 'fourth-sub');
  private shownAt = 0;

  constructor(onChoice: (choice: 'go' | 'punt' | 'fg') => void, guardSeconds: number) {
    const on = (c: 'go' | 'punt' | 'fg') => guarded(guardSeconds, () => this.shownAt, () => onChoice(c))();
    const box = el('div', 'fourth-box panel');
    const go = el('button', 'btn btn-primary btn-big', 'GO FOR IT');
    const punt = el('button', 'btn btn-ghost btn-big', 'PUNT');
    go.dataset.testid = 'fourth-go';
    punt.dataset.testid = 'fourth-punt';
    this.fg.dataset.testid = 'fourth-fg';
    onTap(go, () => on('go'));
    onTap(punt, () => on('punt'));
    onTap(this.fg, () => on('fg'));
    const row = el('div', 'fourth-row');
    row.append(go, punt, this.fg);
    box.append(el('div', 'fourth-title', '4TH DOWN'), this.sub, row);
    this.root.append(box);
    this.root.hidden = true;
  }

  show(sub: string, fgText: string | null): void {
    this.sub.textContent = sub;
    this.fg.hidden = fgText === null;
    this.fg.textContent = fgText ?? '';
    this.shownAt = performance.now();
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
  }
}

/** Short card for things that happen off screen (opponent drive, kicks, quarter breaks). A tap skips it. */
export class InfoCard {
  readonly root = el('div', 'info');
  private readonly title = el('div', 'info-title');
  private readonly body = el('div', 'info-body');

  constructor(onSkip: () => void) {
    const box = el('div', 'info-box panel');
    box.append(this.title, this.body, el('div', 'info-skip', 'TAP TO CONTINUE'));
    this.root.append(box);
    onTap(this.root, () => onSkip());
    this.root.dataset.testid = 'info';
    this.root.hidden = true;
  }

  show(title: string, body: string): void {
    this.title.textContent = title;
    this.body.textContent = body;
    this.root.hidden = false;
    this.root.classList.remove('in');
    void this.root.offsetWidth;
    this.root.classList.add('in');
  }

  hide(): void {
    this.root.hidden = true;
  }
}

/** Final whistle. */
export class FinalCard {
  readonly root = el('div', 'final');
  private readonly title = el('div', 'final-title');
  private readonly score = el('div', 'final-score');
  private readonly line = el('div', 'final-line');

  private shownAt = 0;

  constructor(onAgain: () => void, guardSeconds: number) {
    const box = el('div', 'final-box');
    const again = el('button', 'btn btn-primary btn-big', 'PLAY AGAIN');
    again.dataset.testid = 'again';
    onTap(again, guarded(guardSeconds, () => this.shownAt, onAgain));
    box.append(this.title, this.score, this.line, again);
    this.root.append(box);
    this.root.hidden = true;
  }

  show(title: string, score: string, line: string): void {
    this.title.textContent = title;
    this.score.textContent = score;
    this.line.textContent = line;
    this.shownAt = performance.now();
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
  }
}
